import { useState, useCallback, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Project } from '@/api/client';
import { api, type UpdateProjectData } from '@/api/client';
import { ContextMenu } from '@/components/ui/ContextMenu/ContextMenu';
import { ContextMenuSubmenu } from '@/components/ui/ContextMenu/ContextMenuSubmenu';
import { MenuIcon, ChevronRightIcon, InfoIcon, SendIcon, BookmarkIcon, HistoryIcon } from '@/components/ui/Icons';
import { ProjectChangeHistoryPopover } from '@/features/project-change-history/ProjectChangeHistoryPopover';
import {
  refreshUndoRedoAvailability,
  setLastUndoRedoProjectId,
} from '@/features/project-change-history/projectUndoRedoStore';
import { Switch } from '@/components/ui/Switch/Switch';
import { ContextMenuDivider } from '@/components/ui/ContextMenu/ContextMenuDivider';
import { useToast } from '@/contexts/ToastContext';
import { useProjectEntity } from '@/hooks/useProjectEntity';
import { markProjectUpdatedByMutation } from '@/lib/projectRealtimeDedup';
import {
  captureProjectListRollback,
  mergeSingleProjectInCaches,
  patchProjectListCaches,
} from '@/lib/projectListQueryCache';
import { PROJECTS_TABLE_ACTIVE_IDS_QUERY_KEY } from '@/lib/projectEntityCache';
import { rollbackProjectsAndReconcile } from '@/lib/projectMutationReconcile';
import { applyProjectArchiveOptimistic, applyProjectArchiveSaved } from '@/lib/projectArchiveCache';
import { DeleteProjectAction } from './actions/DeleteProjectAction';

interface ActionsCellProps {
  project: Project;
  projectId: string;
  projectName: string;
  onDelete: (id: string) => void;
  isDeleting: boolean;
  onOpenReviewCheckEmail?: (project: Project) => void;
  onOpenProductionHandoffEmail?: (project: Project) => void;
  onOpenPreArchiveUploadEmail?: (project: Project) => void;
  onOpenMountingCompleteEmail?: (project: Project) => void;
  onOpenCorrectionsAppliedEmail?: (project: Project) => void;
  onOpenProjectReviewedEmail?: (project: Project) => void;
  /** Якорь — кнопка меню; открывает попап «Информация о проекте» на уровне строки */
  onOpenProjectDetails?: (anchorEl: HTMLElement | null) => void;
}

const SUBMENU_CLOSE_DELAY_MS = 150;

export function ActionsCell({
  project,
  projectId,
  projectName,
  onDelete,
  isDeleting,
  onOpenReviewCheckEmail,
  onOpenProductionHandoffEmail,
  onOpenPreArchiveUploadEmail,
  onOpenMountingCompleteEmail,
  onOpenCorrectionsAppliedEmail,
  onOpenProjectReviewedEmail,
  onOpenProjectDetails,
}: ActionsCellProps) {
  const queryClient = useQueryClient();
  const liveProject = useProjectEntity(projectId, project) ?? project;
  const { showSuccess, showError } = useToast();
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [emailSubmenuOpen, setEmailSubmenuOpen] = useState(false);
  const [emailSubmenuEl, setEmailSubmenuEl] = useState<HTMLElement | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyAnchorEl, setHistoryAnchorEl] = useState<HTMLElement | null>(null);
  const detailsButtonRef = useRef<HTMLButtonElement | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleButtonClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    if (isDeleting) return;
    setMenuAnchorEl(e.currentTarget);
    setMenuOpen((prev) => !prev);
  }, [isDeleting]);

  const handleOpenDetails = useCallback(() => {
    setMenuOpen(false);
    setMenuAnchorEl(null);
    onOpenProjectDetails?.(detailsButtonRef.current);
  }, [onOpenProjectDetails]);

  const handleClose = useCallback(() => {
    setMenuOpen(false);
    setMenuAnchorEl(null);
    setEmailSubmenuOpen(false);
    setEmailSubmenuEl(null);
  }, []);

  const handleOpenReviewEmail = useCallback(() => {
    onOpenReviewCheckEmail?.(liveProject);
    handleClose();
  }, [onOpenReviewCheckEmail, liveProject, handleClose]);

  const handleOpenProductionHandoffEmail = useCallback(() => {
    onOpenProductionHandoffEmail?.(liveProject);
    handleClose();
  }, [onOpenProductionHandoffEmail, liveProject, handleClose]);

  const handleOpenPreArchiveUploadEmail = useCallback(() => {
    onOpenPreArchiveUploadEmail?.(liveProject);
    handleClose();
  }, [onOpenPreArchiveUploadEmail, liveProject, handleClose]);

  const handleOpenMountingCompleteEmail = useCallback(() => {
    onOpenMountingCompleteEmail?.(liveProject);
    handleClose();
  }, [onOpenMountingCompleteEmail, liveProject, handleClose]);

  const handleOpenCorrectionsAppliedEmail = useCallback(() => {
    onOpenCorrectionsAppliedEmail?.(liveProject);
    handleClose();
  }, [onOpenCorrectionsAppliedEmail, liveProject, handleClose]);

  const handleOpenProjectReviewedEmail = useCallback(() => {
    onOpenProjectReviewedEmail?.(liveProject);
    handleClose();
  }, [onOpenProjectReviewedEmail, liveProject, handleClose]);

  const showEmailSubmenu = Boolean(
    onOpenReviewCheckEmail ||
      onOpenProductionHandoffEmail ||
      onOpenPreArchiveUploadEmail ||
      onOpenMountingCompleteEmail ||
      onOpenCorrectionsAppliedEmail ||
      onOpenProjectReviewedEmail
  );

  const highlighted = !!liveProject.is_project_highlighted;
  const noEdits = !!liveProject.no_edits;

  const noEditsMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProjectData }) => api.projects.update(id, data),
    onMutate: async ({ id, data }) => {
      const rollback = captureProjectListRollback(queryClient);
      await queryClient.cancelQueries({ queryKey: PROJECTS_TABLE_ACTIVE_IDS_QUERY_KEY });
      const nextFlag = !!data.no_edits;
      patchProjectListCaches(queryClient, (prev) =>
        prev?.map((p) => (p.id === id ? { ...p, no_edits: nextFlag } : p))
      );
      markProjectUpdatedByMutation(id);
      return rollback;
    },
    onError: (err, _vars, ctx) => {
      void rollbackProjectsAndReconcile(queryClient, ctx).catch(() => {});
      showError(err instanceof Error ? err.message : String(err));
    },
    onSuccess: (fresh) => {
      showSuccess(fresh.no_edits ? 'Отмечено «Без правок»' : 'Снят признак «Без правок»');
      mergeSingleProjectInCaches(queryClient, fresh);
      setLastUndoRedoProjectId(fresh.id);
      void refreshUndoRedoAvailability(queryClient, fresh.id).catch(() => {});
    },
  });

  const highlightMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProjectData }) => api.projects.update(id, data),
    onMutate: async ({ id, data }) => {
      const rollback = captureProjectListRollback(queryClient);
      await queryClient.cancelQueries({ queryKey: PROJECTS_TABLE_ACTIVE_IDS_QUERY_KEY });
      const nextFlag = !!data.is_project_highlighted;
      patchProjectListCaches(queryClient, (prev) =>
        prev?.map((p) => (p.id === id ? { ...p, is_project_highlighted: nextFlag } : p))
      );
      markProjectUpdatedByMutation(id);
      return rollback;
    },
    onError: (err, _vars, ctx) => {
      void rollbackProjectsAndReconcile(queryClient, ctx).catch(() => {});
      showError(err instanceof Error ? err.message : String(err));
    },
    onSuccess: (fresh) => {
      showSuccess('Выделение обновлено');
      mergeSingleProjectInCaches(queryClient, fresh);
      setLastUndoRedoProjectId(fresh.id);
      void refreshUndoRedoAvailability(queryClient, fresh.id).catch(() => {});
    },
  });

  const archiveToggleMutation = useMutation({
    mutationFn: ({ id, is_archived }: { id: string; is_archived: boolean }) =>
      api.projects.update(id, { is_archived }),
    onMutate: ({ id, is_archived }) => applyProjectArchiveOptimistic(queryClient, id, is_archived, liveProject),
    onError: (err, _vars, ctx) => {
      void rollbackProjectsAndReconcile(queryClient, ctx).catch(() => {});
      showError(err instanceof Error ? err.message : String(err));
    },
    onSuccess: (fresh) => {
      showSuccess(fresh.is_archived ? 'Проект убран в архив' : 'Проект восстановлен из архива');
      applyProjectArchiveSaved(queryClient, fresh);
      setLastUndoRedoProjectId(fresh.id);
      void refreshUndoRedoAvailability(queryClient, fresh.id).catch(() => {});
    },
  });

  const handleToggleArchive = useCallback(() => {
    handleClose();
    archiveToggleMutation.mutate({ id: projectId, is_archived: !liveProject.is_archived });
  }, [archiveToggleMutation, handleClose, liveProject.is_archived, projectId]);

  const handleToggleHighlight = useCallback(() => {
    handleClose();
    highlightMutation.mutate({
      id: projectId,
      data: { is_project_highlighted: !highlighted },
    });
  }, [handleClose, highlightMutation, highlighted, projectId]);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const scheduleSubmenuClose = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setEmailSubmenuOpen(false);
      setEmailSubmenuEl(null);
      closeTimerRef.current = null;
    }, SUBMENU_CLOSE_DELAY_MS);
  }, [clearCloseTimer]);

  const cancelSubmenuClose = useCallback(() => {
    clearCloseTimer();
  }, [clearCloseTimer]);

  return (
    <>
      <div className="projects-table__actions">
        <button
          ref={detailsButtonRef}
          type="button"
          className="projects-table__action-btn projects-table__actions-btn"
          onClick={handleButtonClick}
          disabled={isDeleting}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-label="Меню"
        >
          <MenuIcon size={20} />
        </button>
      </div>
      <ContextMenu
        isOpen={menuOpen}
        onClose={handleClose}
        anchorPosition={null}
        anchorEl={menuAnchorEl}
        alignY="center"
      >
        <ul className="context-menu__list" role="menu">
          <li className="context-menu__list-item">
            <button
              type="button"
              className="context-menu__action"
              onClick={handleOpenDetails}
            >
              <InfoIcon size={18} />
              <span className="context-menu__action-text">Информация о проекте</span>
            </button>
          </li>
          <li className="context-menu__list-item">
            <button
              type="button"
              className="context-menu__action"
              onClick={() => {
                setHistoryAnchorEl(menuAnchorEl ?? detailsButtonRef.current);
                handleClose();
                setLastUndoRedoProjectId(projectId);
                setHistoryOpen(true);
              }}
            >
              <HistoryIcon size={18} />
              <span className="context-menu__action-text">Посмотреть изменения</span>
            </button>
          </li>
          <li className="context-menu__list-item">
            <button
              type="button"
              className="context-menu__action"
              onClick={handleToggleArchive}
              disabled={archiveToggleMutation.isPending}
            >
              <i
                className={liveProject.is_archived ? 'fa-solid fa-box-open' : 'fa-solid fa-box-archive'}
                aria-hidden
              />
              <span className="context-menu__action-text">
                {liveProject.is_archived ? 'Восстановить из архива' : 'Убрать в архив'}
              </span>
            </button>
          </li>
          <li className="context-menu__list-item">
            <button
              type="button"
              className="context-menu__action"
              onClick={handleToggleHighlight}
              disabled={highlightMutation.isPending}
            >
              {highlighted ? (
                <i className="fa-regular fa-bookmark" aria-hidden />
              ) : (
                <BookmarkIcon size={18} />
              )}
              <span className="context-menu__action-text">
                {highlighted ? 'Снять выделение' : 'Выделить проект'}
              </span>
            </button>
          </li>
          {showEmailSubmenu && (
            <li
              className="context-menu__list-item"
              onMouseEnter={(e) => {
                cancelSubmenuClose();
                setEmailSubmenuOpen(true);
                setEmailSubmenuEl(e.currentTarget);
              }}
              onMouseLeave={scheduleSubmenuClose}
            >
              <button type="button" className="context-menu__action context-menu__action_with-sub">
                <SendIcon size={18} />
                <span className="context-menu__action-text">Отправить письмо</span>
                <ChevronRightIcon size={16} className="context-menu__action-chevron" />
              </button>
              {emailSubmenuOpen && emailSubmenuEl && (
                <ContextMenuSubmenu
                  anchorEl={emailSubmenuEl}
                  onMouseEnter={cancelSubmenuClose}
                  onMouseLeave={() => {
                    setEmailSubmenuOpen(false);
                    setEmailSubmenuEl(null);
                  }}
                >
                  <ul className="context-menu__list">
                    {onOpenPreArchiveUploadEmail && (
                      <li className="context-menu__list-item">
                        <button
                          type="button"
                          className="context-menu__action"
                          onClick={handleOpenPreArchiveUploadEmail}
                        >
                          <span className="context-menu__action-text">Загружен в предархив</span>
                        </button>
                      </li>
                    )}
                    {onOpenCorrectionsAppliedEmail && (
                      <li className="context-menu__list-item">
                        <button
                          type="button"
                          className="context-menu__action"
                          onClick={handleOpenCorrectionsAppliedEmail}
                        >
                          <span className="context-menu__action-text">Правки внесены</span>
                        </button>
                      </li>
                    )}
                    {onOpenProjectReviewedEmail && (
                      <li className="context-menu__list-item">
                        <button
                          type="button"
                          className="context-menu__action"
                          onClick={handleOpenProjectReviewedEmail}
                        >
                          <span className="context-menu__action-text">Проверка завершена</span>
                        </button>
                      </li>
                    )}
                    {onOpenReviewCheckEmail && (
                      <li className="context-menu__list-item">
                        <button
                          type="button"
                          className="context-menu__action"
                          onClick={handleOpenReviewEmail}
                        >
                          <span className="context-menu__action-text">Отправить на проверку</span>
                        </button>
                      </li>
                    )}
                    {onOpenMountingCompleteEmail && (
                      <li className="context-menu__list-item">
                        <button
                          type="button"
                          className="context-menu__action"
                          onClick={handleOpenMountingCompleteEmail}
                        >
                          <span className="context-menu__action-text">Монтаж завершён</span>
                        </button>
                      </li>
                    )}
                    {onOpenProductionHandoffEmail && (
                      <li className="context-menu__list-item">
                        <button
                          type="button"
                          className="context-menu__action"
                          onClick={handleOpenProductionHandoffEmail}
                        >
                          <span className="context-menu__action-text">Передать в производство</span>
                        </button>
                      </li>
                    )}
                  </ul>
                </ContextMenuSubmenu>
              )}
            </li>
          )}
          <ContextMenuDivider inList listSpacing />
          <li className="context-menu__list-item">
            <Switch
              className="context-menu__label"
              checked={noEdits}
              disabled={noEditsMutation.isPending}
              onCheckedChange={(next) => {
                noEditsMutation.mutate({ id: projectId, data: { no_edits: next } });
              }}
              onClick={(e) => e.stopPropagation()}
              label={<span className="context-menu__label-text context-menu__label-text_uppercase">Без правок</span>}
            />
          </li>
          <ContextMenuDivider inList listSpacing />
          <li className="context-menu__list-item">
            <DeleteProjectAction
              projectId={projectId}
              projectName={projectName}
              onDelete={onDelete}
              onClose={handleClose}
              disabled={isDeleting}
            />
          </li>
        </ul>
      </ContextMenu>
      <ProjectChangeHistoryPopover
        open={historyOpen}
        onClose={() => {
          setHistoryOpen(false);
          setHistoryAnchorEl(null);
        }}
        anchorEl={historyAnchorEl ?? detailsButtonRef.current}
        anchorPlacement="beside-menu"
        projectId={projectId}
        projectName={projectName}
      />
    </>
  );
}
